/**
 * Criterion Mapping — Phase 3 of the 4-phase OBS appointment (version switch).
 *
 * The Behavioural Observation module has two Criterion Mapping experiences that
 * are gated on the patient's immutable stamped workflow config:
 *
 *   • v1 (legacy, default): the original task-by-task mapping table. Rendered
 *     for every autism patient whose config is NOT obsFlow.version === 'v2'
 *     (i.e. all existing / in-flight patients and any org not yet on v2).
 *     Implementation: 3_CriterionMappingLegacy.js.
 *
 *   • v2 (redesign): criteria-by-criteria review of notes tagged upstream in
 *     Task Administration and ADOS-informed Coding, with a single evidence
 *     decision per DSM criterion plus Self-Reported / Other Behaviours buckets.
 *     Rendered only when obsFlow.version === 'v2'.
 *     Implementation: 3_CriterionMappingV2.js.
 *
 * WHY A SINGLE SWITCH (not two routes): keeping one route + one page entry
 * means routes.js, breadcrumbs, and every smart-resume/deep-link target stay
 * identical for both cohorts — the only thing that changes is which panel the
 * clinician sees. The switch is safe to evaluate synchronously because the
 * route is wrapped in ModularPatientRouteWrapper, which already gates rendering
 * until the patient header has loaded, so `isObsV2` never flashes the wrong
 * variant (which would remount the child and drop its local UI state).
 */

import React from 'react';
import { useContext } from 'react';
import { ModularPatientDataContext } from '../../../../context/ModularPatientDataContext';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import CriterionMappingLegacy from './3_CriterionMappingLegacy';
import CriterionMappingV2 from './3_CriterionMappingV2';

export default function CriterionMapping() {
  const { header } = useContext(ModularPatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);

  return wf?.isObsV2 ? <CriterionMappingV2 /> : <CriterionMappingLegacy />;
}
